import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import Button from '@components/common/Button/Button.component'
import AccountsHeaderActionButton from './AccountsHeaderActionButton.component'
import AddOverrideModal, {
  type ScheduleOverrideFormValues,
} from './AddOverrideModal.component'
import WorkingDayBadge from '@components/common/WorkingDayBadge/WorkingDayBadge.component'
import {
  DayOfWeek,
  type WorkingSchedule,
  ProviderData,
} from '@dataTypes/provider.types'
import { TIME_OPTIONS } from '@constants/Providers'
import { AccountsSection } from '@constants/Accounts'
import { useProfile } from '@pages/Accounts/AccountsProfile.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useClinicOperatories } from '@hooks'
import { fetchAllOperatories } from '@store/actions/operatory.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { formatMilitaryTimeTo12Hour } from '@utils/time.util'
import { useAccountsHeader } from '@pages/Accounts/AccountsHeader.context'

import { areValuesEqual } from '@utils/settings.util'
import './WorkInfo.component.css'

type DurationOption = '1 month' | '3 months' | '6 months' | '12 months'

type WorkScheduleOverride = ScheduleOverrideFormValues & { id: string }

type DayShiftTimes = { start: string; end: string }

const displayLetters = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const dayOrder: DayOfWeek[] = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
]

const DEFAULT_PER_DAY_SHIFT: DayShiftTimes = {
  start: '08:00:00',
  end: '17:00:00',
}

function buildInitialSelectedDays(): Record<DayOfWeek, boolean> {
  return dayOrder.reduce(
    (acc, d) => ({
      ...acc,
      [d]: d !== DayOfWeek.SATURDAY && d !== DayOfWeek.SUNDAY,
    }),
    {} as Record<DayOfWeek, boolean>,
  )
}

function buildInitialPerDayShifts(): Record<DayOfWeek, DayShiftTimes> {
  return dayOrder.reduce(
    (acc, d) => ({ ...acc, [d]: { ...DEFAULT_PER_DAY_SHIFT } }),
    {} as Record<DayOfWeek, DayShiftTimes>,
  )
}

function newOverrideId(): string {
  return `ov-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

type WorkFormState = {
  sameWorkingHours: boolean
  shiftStartTime: string
  shiftEndTime: string
  selectedDays: Record<DayOfWeek, boolean>
  perDayShifts: Record<DayOfWeek, DayShiftTimes>
  operatoryId: string
  overrides: WorkScheduleOverride[]
}

const buildWorkStateFromProfile = (profile: ProviderData): WorkFormState => {
  const wh = profile.workingSchedules ?? []
  const nextSel = buildInitialSelectedDays()
  const nextPer = buildInitialPerDayShifts()
  for (const d of dayOrder) {
    nextSel[d] = false
  }
  for (const row of wh) {
    const dow = row.day_of_week as DayOfWeek
    if (dayOrder.includes(dow)) {
      nextSel[dow] = true
      nextPer[dow] = {
        start: row.start_time,
        end: row.end_time,
      }
    }
  }
  return {
    sameWorkingHours: profile.sameWorkingHours ?? true,
    shiftStartTime: profile.shiftStartTime ?? '',
    shiftEndTime: profile.shiftEndTime ?? '',
    selectedDays: nextSel,
    perDayShifts: nextPer,
    operatoryId: profile.operatoryId ?? '',
    overrides: profile.workOverrides ?? [],
  }
}

const workToCompareValues = (state: WorkFormState): string[] => [
  String(state.sameWorkingHours),
  state.shiftStartTime,
  state.shiftEndTime,
  state.operatoryId,
  ...dayOrder.flatMap((day) => [
    String(state.selectedDays[day]),
    state.perDayShifts[day].start,
    state.perDayShifts[day].end,
  ]),
  JSON.stringify(
    [...state.overrides].sort((a, b) =>
      `${a.date}-${a.start_time}-${a.id}`.localeCompare(
        `${b.date}-${b.start_time}-${b.id}`,
      ),
    ),
  ),
]

function formatOverrideDisplayDate(isoDate: string): string {
  if (!isoDate) return ''
  const [y, m, d] = isoDate.split('-').map(Number)
  if (!y || !m || !d) return isoDate
  const date = new Date(y, m - 1, d)
  const weekday = date.toLocaleDateString(undefined, { weekday: 'long' })
  const mdY = date.toLocaleDateString(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
  return `${weekday} ${mdY}`
}

const WorkInfo: React.FC = () => {
  const { setHeaderButtons } = useAccountsHeader()
  const { profile, savePatch, loadState } = useProfile()
  const { showToast } = useNotificationToast()
  const dispatch = useAppDispatch()
  const operatories = useAppSelector((state) => state.operatory.data)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id

  useClinicOperatories()

  useEffect(() => {
    if (!selectedClinic) {
      void dispatch(fetchAllOperatories())
    }
  }, [dispatch, selectedClinic])

  const operatoryOptions = useMemo(
    () =>
      operatories
        .filter((o) => (clinicId ? o.clinic_id === clinicId : true))
        .map((o) => ({ id: o.operatory_id, name: o.name })),
    [operatories, clinicId],
  )

  const [sameWorkingHours, setSameWorkingHours] = useState<boolean>(true)
  const [duration, setDuration] = useState<DurationOption>('1 month')

  const [selectedDays, setSelectedDays] = useState<Record<DayOfWeek, boolean>>(
    () => buildInitialSelectedDays(),
  )
  const [shiftStartTime, setShiftStartTime] = useState<string>('')
  const [shiftEndTime, setShiftEndTime] = useState<string>('')
  const [perDayShifts, setPerDayShifts] = useState<
    Record<DayOfWeek, DayShiftTimes>
  >(() => buildInitialPerDayShifts())
  const [operatoryId, setOperatoryId] = useState<string>('')

  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [overrideModalMode, setOverrideModalMode] = useState<'add' | 'edit'>(
    'add',
  )
  const [editingOverrideIndex, setEditingOverrideIndex] = useState<
    number | null
  >(null)
  const [overrides, setOverrides] = useState<WorkScheduleOverride[]>([])

  const applyWorkStateFromProfile = useCallback((): void => {
    if (!profile) return
    const next = buildWorkStateFromProfile(profile)
    setSameWorkingHours(next.sameWorkingHours)
    setShiftStartTime(next.shiftStartTime)
    setShiftEndTime(next.shiftEndTime)
    setOperatoryId(next.operatoryId)
    setSelectedDays(next.selectedDays)
    setPerDayShifts(next.perDayShifts)
    setOverrides(next.overrides)
  }, [profile])

  useEffect(() => {
    applyWorkStateFromProfile()
  }, [applyWorkStateFromProfile])

  const handleCancel = useCallback((): void => {
    applyWorkStateFromProfile()
  }, [applyWorkStateFromProfile])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!profile?.providerId) return
    const rows: WorkingSchedule[] = []
    for (const d of dayOrder) {
      if (!selectedDays[d]) continue
      const start = sameWorkingHours ? shiftStartTime : perDayShifts[d].start
      const end = sameWorkingHours ? shiftEndTime : perDayShifts[d].end
      if (!start || !end) continue
      rows.push({
        day_of_week: d,
        start_time: start,
        end_time: end,
      })
    }
    const extra: WorkingSchedule[] = overrides.map((o) => ({
      day_of_week: o.date,
      start_time: o.start_time,
      end_time: o.end_time,
    }))
    const ok = await savePatch(AccountsSection.Work, {
      providerId: profile.providerId,
      sameWorkingHours,
      shiftStartTime,
      shiftEndTime,
      workingSchedules: [...rows, ...extra],
      operatoryId: operatoryId || undefined,
      workStatus: profile.workStatus,
    })
    showToast?.(
      ok
        ? createSuccessToast('Saved', 'Work schedule updated.')
        : createFailedToast('Error', 'Could not save work schedule.'),
    )
  }, [
    profile,
    savePatch,
    showToast,
    selectedDays,
    sameWorkingHours,
    shiftStartTime,
    shiftEndTime,
    perDayShifts,
    operatoryId,
    overrides,
  ])

  const hasChanges = useMemo(() => {
    if (!profile) return false
    const baseline = buildWorkStateFromProfile(profile)
    const current: WorkFormState = {
      sameWorkingHours,
      shiftStartTime,
      shiftEndTime,
      selectedDays,
      perDayShifts,
      operatoryId,
      overrides,
    }
    return !areValuesEqual(
      workToCompareValues(current),
      workToCompareValues(baseline),
    )
  }, [
    profile,
    sameWorkingHours,
    shiftStartTime,
    shiftEndTime,
    selectedDays,
    perDayShifts,
    operatoryId,
    overrides,
  ])

  const actionsDisabled = loadState !== 'ready' || !profile

  useEffect(() => {
    setHeaderButtons(
      <AccountsHeaderActionButton
        onClick={() => {
          if (actionsDisabled || !hasChanges) return
          void handleSave()
        }}
        onCancel={handleCancel}
        cancelDisabled={!hasChanges || actionsDisabled}
        saveDisabled={!hasChanges || actionsDisabled}
      >
        Save changes
      </AccountsHeaderActionButton>,
    )
    return () => setHeaderButtons(null)
  }, [setHeaderButtons, handleSave, handleCancel, hasChanges, actionsDisabled])

  const toggleDay = useCallback((day: DayOfWeek): void => {
    setSelectedDays((prev) => ({ ...prev, [day]: !prev[day] }))
  }, [])

  const setDayShiftTime = useCallback(
    (day: DayOfWeek, field: keyof DayShiftTimes, value: string): void => {
      setPerDayShifts((prev) => ({
        ...prev,
        [day]: { ...prev[day], [field]: value },
      }))
    },
    [],
  )

  const openAddOverrideModal = useCallback((): void => {
    setOverrideModalMode('add')
    setEditingOverrideIndex(null)
    setShowOverrideModal(true)
  }, [])

  const closeOverrideModal = useCallback((): void => {
    setShowOverrideModal(false)
    setEditingOverrideIndex(null)
  }, [])

  const handleOverrideModalSubmit = useCallback(
    (values: ScheduleOverrideFormValues): void => {
      setOverrides((prev) => {
        if (editingOverrideIndex !== null) {
          const next = [...prev]
          const existing = next[editingOverrideIndex]
          if (existing) {
            next[editingOverrideIndex] = {
              id: existing.id,
              ...values,
            }
          }
          return next
        }
        return [...prev, { id: newOverrideId(), ...values }]
      })
      closeOverrideModal()
    },
    [editingOverrideIndex, closeOverrideModal],
  )

  const removeOverrideById = useCallback((id: string): void => {
    setOverrides((prev) => prev.filter((x) => x.id !== id))
  }, [])

  const editOverrideById = useCallback(
    (id: string): void => {
      const idx = overrides.findIndex((x) => x.id === id)
      if (idx < 0) return
      setOverrideModalMode('edit')
      setEditingOverrideIndex(idx)
      setShowOverrideModal(true)
    },
    [overrides],
  )

  const overrideModalInitialValues =
    useMemo((): ScheduleOverrideFormValues | null => {
      if (editingOverrideIndex === null) return null
      const o = overrides[editingOverrideIndex]
      if (!o) return null
      return {
        name: o.name,
        date: o.date,
        start_time: o.start_time,
        end_time: o.end_time,
      }
    }, [editingOverrideIndex, overrides])

  const sortedOverrides = useMemo(() => {
    return [...overrides].sort((a, b) => {
      const dateCmp = a.date.localeCompare(b.date)
      if (dateCmp !== 0) return dateCmp
      return a.start_time.localeCompare(b.start_time)
    })
  }, [overrides])

  return (
    <div className="accounts-work-info">
      <AddOverrideModal
        show={showOverrideModal}
        onHide={closeOverrideModal}
        onSubmit={handleOverrideModalSubmit}
        initialValues={overrideModalInitialValues}
        mode={overrideModalMode}
      />

      <Row className="gx-2 mb-2">
        <Col md={6}>
          <Form.Group controlId="accountsSameWorkingHours">
            <Form.Label>Same working hours</Form.Label>
            <Form.Select
              value={sameWorkingHours ? 'yes' : 'no'}
              onChange={(e) => setSameWorkingHours(e.target.value === 'yes')}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="accountsWorkDuration">
            <Form.Label>Duration</Form.Label>
            <Form.Select
              value={duration}
              disabled={!sameWorkingHours}
              onChange={(e) => setDuration(e.target.value as DurationOption)}
            >
              <option value="1 month">1 month</option>
              <option value="3 months">3 months</option>
              <option value="6 months">6 months</option>
              <option value="12 months">12 months</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Form.Label>Working days</Form.Label>

      {sameWorkingHours ? (
        <>
          <Row className="gx-2 mb-2">
            <Col md={12}>
              <Form.Group controlId="accountsWorkingDays">
                <div className="modal-working-days-container accounts-working-days">
                  {dayOrder.map((day, index) => (
                    <WorkingDayBadge
                      key={day}
                      day={displayLetters[index] ?? ''}
                      isAvailable={!!selectedDays[day]}
                      onClick={() => toggleDay(day)}
                      title={day}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-2 mb-2">
            <Col md={6}>
              <Form.Group controlId="accountsShiftStartTime">
                <Form.Label>Shift start</Form.Label>
                <Form.Select
                  value={shiftStartTime}
                  onChange={(e) => setShiftStartTime(e.target.value)}
                >
                  <option value="">Select</option>
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="accountsShiftEndTime">
                <Form.Label>Shift end</Form.Label>
                <Form.Select
                  value={shiftEndTime}
                  onChange={(e) => setShiftEndTime(e.target.value)}
                >
                  <option value="">Select</option>
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <div className="row gx-2 mb-2">
            {dayOrder.map((day) => {
              const active = !!selectedDays[day]
              return (
                <div key={day} className="col-12">
                  <Row
                    className={`accounts-per-day-row gx-2 align-items-center mb-2 ${
                      active ? 'is-active' : 'is-inactive'
                    }`}
                  >
                    <Col xs="auto" className="d-flex align-items-center pt-1">
                      <Form.Check
                        type="switch"
                        id={`accounts-day-toggle-${day}`}
                        checked={active}
                        onChange={() => toggleDay(day)}
                        aria-label={`${day} working`}
                      />
                    </Col>
                    <Col xs="auto" className="accounts-per-day-name">
                      {day}
                    </Col>
                    <Col className="accounts-per-day-times d-flex align-items-center justify-content-end flex-wrap gap-2 min-w-0">
                      <Form.Select
                        size="sm"
                        className="accounts-per-day-shift-select accounts-per-day-shift-input"
                        value={perDayShifts[day].start}
                        disabled={!active}
                        onChange={(e) =>
                          setDayShiftTime(day, 'start', e.target.value)
                        }
                        aria-label={`${day} shift start`}
                      >
                        <option value="">Select</option>
                        {TIME_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="accounts-per-day-to text-muted">to</span>
                      <Form.Select
                        size="sm"
                        className="accounts-per-day-shift-select accounts-per-day-shift-input"
                        value={perDayShifts[day].end}
                        disabled={!active}
                        onChange={(e) =>
                          setDayShiftTime(day, 'end', e.target.value)
                        }
                        aria-label={`${day} shift end`}
                      >
                        <option value="">Select</option>
                        {TIME_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                </div>
              )
            })}
          </div>
        </>
      )}

      <Row className="gx-2 mb-2">
        <Col md={12}>
          <Form.Group controlId="accountsOperatoryRooms">
            <div className="accounts-work-info-assigned-operatories-title">
              Assigned operatories
            </div>
            <Form.Label>Operatory rooms</Form.Label>
            <Form.Select
              value={operatoryId}
              onChange={(e) => setOperatoryId(e.target.value)}
            >
              <option value="">Select</option>
              {operatoryOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 align-items-center mb-2">
        <Col>
          <div className="accounts-work-info-section-title">
            Schedule overrides
          </div>
        </Col>
        <Col className="text-end">
          <Button
            size="sm"
            className="button-secondary"
            onClick={openAddOverrideModal}
            type="button"
          >
            Add override
          </Button>
        </Col>
      </Row>

      <Row className="gx-2">
        {sortedOverrides.map((o) => (
          <Col md={4} key={o.id}>
            <div className="accounts-override-card">
              <div className="accounts-override-card-inner">
                <div className="accounts-override-card-body">
                  <div className="accounts-override-title">
                    {o.name.trim() ? o.name : 'Business list'}
                  </div>
                  <div className="accounts-override-time">
                    <span className="accounts-override-dow">
                      {formatOverrideDisplayDate(o.date)}
                    </span>{' '}
                    <span className="accounts-override-time-range">
                      {formatMilitaryTimeTo12Hour(o.start_time)}
                      {' - '}
                      {formatMilitaryTimeTo12Hour(o.end_time)}
                    </span>
                  </div>
                  {sameWorkingHours &&
                    shiftStartTime &&
                    shiftEndTime &&
                    o.start_time === shiftStartTime &&
                    o.end_time === shiftEndTime && (
                      <div className="accounts-override-hint">
                        Same as default shift
                      </div>
                    )}
                </div>
                <div className="accounts-override-card-actions">
                  <ActionButtons
                    showView={false}
                    showMoreOptions={false}
                    onEdit={() => editOverrideById(o.id)}
                    onDelete={() => removeOverrideById(o.id)}
                  />
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  )
}

export default WorkInfo
